/* @jsx h */
import { defineComponent, h, ref, onMounted } from "vue";
import { formatPrice } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth, useToast, useTrpc } from "@app/vue/providers/index";

type OpenCheckoutRow = {
  id: string;
  service_method: string;
  table_id: number | null;
  payment_mode: "split" | "partial" | null;
  totals: { subtotal?: number } | null;
  created_at: string;
  collected_amount: number;
  payment_count: number;
};

export type OpenCheckoutsSectionProps = {
  onResume: (id: string) => void | Promise<void>;
};

/**
 * Crash-recovery surface: lists staff_checkouts left `open` (app crashed
 * mid-split, terminal leg in flight, browser closed) so the cashier can resume
 * and finish, or cancel them. See docs/plans/checkout_editor/.
 */
export const OpenCheckoutsSection =
  defineComponent<OpenCheckoutsSectionProps>({
    name: "OpenCheckoutsSection",
    props: ["onResume"],
    setup(props) {
      const trpc = useTrpc();
      const auth = useAuth();
      const toast = useToast();
      const { t } = useLocalization();

      const rows = ref<OpenCheckoutRow[]>([]);
      const loading = ref(true);
      const busyId = ref<string | null>(null);
      const currency = () => auth.user.value?.restaurant.currency || "CHF";

      const reload = async () => {
        try {
          loading.value = true;
          rows.value = (await trpc.staffCheckouts.listOpen.query()) as any;
        } catch (e) {
          console.error("listOpen failed", e);
        } finally {
          loading.value = false;
        }
      };

      onMounted(reload);

      const handleCancel = async (row: OpenCheckoutRow) => {
        // Cancelling a checkout that already holds collected legs voids those
        // payments (irreversibly) — gate that behind an explicit confirmation.
        // A checkout with nothing collected cancels straight away.
        const hasCollected = row.payment_count > 0;
        if (hasCollected) {
          const ok = window.confirm(
            t("reports.tx.open_checkouts.void_confirm") ||
              "This checkout has collected payments. Cancelling will VOID them. Continue?",
          );
          if (!ok) return;
        }
        busyId.value = row.id;
        try {
          await trpc.staffCheckouts.cancel.mutate({
            id: row.id,
            voidCollected: hasCollected,
          });
          await reload();
        } catch (e: any) {
          toast.showToast(e?.message || "Failed to cancel checkout", {
            type: "error",
          });
        } finally {
          busyId.value = null;
        }
      };

      const handleResume = async (id: string) => {
        busyId.value = id;
        try {
          await props.onResume(id);
        } finally {
          busyId.value = null;
        }
      };

      return () => {
        if (loading.value && rows.value.length === 0) return null;
        if (rows.value.length === 0) return null;

        return (
          <div class="border-b border-base-content/10 p-3 lg:p-4 bg-base-200/40">
            <div class="flex items-center gap-2 mb-2 text-accent">
              <span class="material-symbols-outlined text-xl">pending_actions</span>
              <span class="font-semibold">
                {t("reports.tx.open_checkouts.title") || "Open checkouts"}
              </span>
              <span class="badge badge-sm badge-neutral">{rows.value.length}</span>
            </div>

            <div class="space-y-2">
              {rows.value.map((row) => {
                const total = row.totals?.subtotal ?? 0;
                const remaining = total - row.collected_amount;
                const isBusy = busyId.value === row.id;
                return (
                  <div
                    key={row.id}
                    class="flex items-center justify-between gap-3 p-3 rounded-lg bg-base-100 border border-base-content/10"
                  >
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2 text-sm text-accent">
                        <span class="capitalize font-medium">
                          {row.service_method}
                        </span>
                        {row.payment_mode && (
                          <span class="badge badge-xs badge-outline capitalize">
                            {row.payment_mode}
                          </span>
                        )}
                        <span class="text-base-content/50 text-xs whitespace-nowrap">
                          {new Date(row.created_at).toLocaleDateString()} ·{" "}
                          {new Date(row.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <div class="text-xs text-base-content/60 mt-0.5">
                        {formatPrice(row.collected_amount, true, 2, currency())}{" "}
                        {t("reports.tx.open_checkouts.collected_of") || "of"}{" "}
                        {formatPrice(total, true, 2, currency())} ·{" "}
                        {row.payment_count}{" "}
                        {t("reports.tx.open_checkouts.payments") || "payments"} ·{" "}
                        {formatPrice(remaining, true, 2, currency())}{" "}
                        {t("tables.checkout.remaining") || "left"}
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        class="btn btn-xs btn-ghost text-error"
                        disabled={isBusy}
                        onClick={() => handleCancel(row)}
                      >
                        {t("general.words.cancel") || "Cancel"}
                      </button>
                      <button
                        class="btn btn-xs btn-primary"
                        disabled={isBusy}
                        onClick={() => handleResume(row.id)}
                      >
                        <span class="material-symbols-outlined text-sm">
                          play_arrow
                        </span>
                        {t("reports.tx.open_checkouts.resume") || "Resume"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      };
    },
  });
